import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Form, Input, Select } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { dateFormat, filterOptions } from "./variables";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE, USER_TENANT_NAME } from "utils/constants";
import { getEndOfTheDay, getStartOfTheDay } from "components/Common/formatter";
import moment from "moment/moment";
import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";

const { Option } = Select;
const { RangePicker } = DatePicker;

const useStyles = makeStyles({
  root: { width: "250px!important" },

  filtersContainer: {
    display: "flex",
    alignItems: "center",
    height: 60,
    overflow: "auto",

    "@media (max-width: 576px)": {
      height: "auto",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      gap: 10,
    },

    "& .ant-select-selection-overflow": {
      flexWrap: "nowrap",
    },
  },
  value: {
    width: "auto !important",
  },
  range: {
    width: "100px!important",
  },
});

const CustomerBlacklistFilter = ({ onFilter }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const { filters } = useStoreFilters(FILTER_PAGE.CUSTOMER_BLACKLIST);
  const [activeFilter, setFilter] = useState(null);

  const {
    list: { depot, blacklistAddingReason },
  } = useSelector(DictionariesSelector);

  const handleFilterChange = useCallback(
    value => {
      setFilter(value);
      form.setFieldsValue({
        type: value,
        value: null,
      });
    },
    [form]
  );

  useEffect(() => {
    const { pageSize, ...dataToSet } = filters;
    if (filters?.blacklistedAtFrom) {
      setFilter("date");
      form.setFieldsValue({
        type: "date",
        value: [moment(filters?.blacklistedAtFrom), moment(filters?.blacklistedAtTo)],
      });
      return;
    }
    if (!!Object.keys(dataToSet)[0]) {
      setFilter(Object.keys(dataToSet)[0]);
      form.setFieldsValue({
        value: dataToSet[Object.keys(dataToSet)[0]],
        type: Object.keys(dataToSet)[0],
      });
    }
  }, [filters]);

  const onFinish = useCallback(
    values => {
      const normalizedFilters = !!filters?.pageSize ? { pageSize: filters.pageSize } : {};
      if (!(values.value && !values.type)) {
        if (values?.type === "date") {
          const filter = {};
          filter.blacklistedAtFrom = getStartOfTheDay(moment(values?.value[0]));
          filter.blacklistedAtTo = getEndOfTheDay(moment(values?.value[1]));
          onFilter(filter);
          return;
        }
        onFilter({ ...normalizedFilters, [values.type]: values.value?.trim() });
      }
    },
    [activeFilter, filters]
  );

  return (
    <>
      <div className="custom-filter">
        <Form form={form} layout="inline" onFinish={onFinish}>
          <div className={classes.filtersContainer}>
            <Form.Item name="type">
              <Select
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                size="default"
                placeholder="Select type"
                allowClear
                onChange={handleFilterChange}
              >
                {filterOptions.length > 0 &&
                  filterOptions.map(item => (
                    <Option key={item.value} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            {!!activeFilter && <div className="filter-divider" />}

            {activeFilter === "date" && (
              <Form.Item
                name="value"
                rules={[{ required: true, message: "Please select date range" }]}
                className={classes.root}
              >
                <RangePicker inputReadOnly={true} format={dateFormat} size={"default"} />
              </Form.Item>
            )}

            {activeFilter === "customerId" && (
              <>
                <Form.Item rules={[{ required: true, message: "Please select value" }]} name="value">
                  <Input placeholder={"Enter value"} allowClear />
                </Form.Item>
              </>
            )}

            {activeFilter === "customerDepot" && (
              <Form.Item name="value">
                <Select
                  showSearch
                  placeholder={"Select value"}
                  rules={[{ required: true, message: "Please select value" }]}
                >
                  {depot[USER_TENANT_NAME()] &&
                    uniq(depot[USER_TENANT_NAME()])
                      ?.sort()
                      ?.map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            )}

            {activeFilter === "addReason" && (
              <Form.Item name="value">
                <Select
                  showSearch
                  placeholder={"Select value"}
                  rules={[{ required: true, message: "Please select value" }]}
                >
                  {blacklistAddingReason?.map(item => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </div>

          <CustomButton type="primary" htmlType="submit" text="Filter" size="small" />
        </Form>
      </div>
    </>
  );
};

export default CustomerBlacklistFilter;
